import React, {useState, useMemo} from 'react';
import {
    Grid,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    Button,
    Modal,
    TextField,
    Container,
    Paper,
    Typography,
    Box,
    Stack
} from '@mui/material';
import CalendarTable from '../../components/calendarAll';
import {uk} from 'date-fns/locale/uk';
import {DateRangePicker} from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import {addDays, addMonths, format} from 'date-fns';
import DoneIcon from '@mui/icons-material/Done';
import api from '../../hooks/useApi';
import {useLoaderData, Await, defer, Form} from 'react-router-dom';
import ErrorBoundary from '../../components/errorBoundary';
import CenteredSpinner from '../../components/centerVPspinner';
import PageTitle from '../../components/PageTitle';
import {requirePartnerOrSuperAdmin} from '../../auth/guards';

export async function loader({params, request}) {
    const authResult = await requirePartnerOrSuperAdmin({request, params});
    if (authResult instanceof Response) return authResult;
    const url = new URL(request.url);
    const searchParams = new URLSearchParams(url.search);
    const searchParamsObject = Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
        if (acc[key]) {
            acc[key].push(value);
        } else {
            acc[key] = [value];
        }
        return acc;
    }, {});

    const to_string = searchParams.toString();
    const fetch_url = `/api/hotel_obj/get/${to_string.length ? `?${to_string}` : ''}`;
    const hotelObjPromise = await api.get(fetch_url);
    hotelObjPromise.data.data.forEach(el => {
        el.ctype = 'hotel';
        el.children_obj?.forEach(c => c.ctype = 'hotel_children')
    })
    // console.log(hotelObjPromise.data.data)
    return defer({
        //hotel_obj: hotelObjPromise
        currentFilter: searchParamsObject,
        promise: hotelObjPromise,
        int_filters: ['square', 'guest_number', 'guest_number_adults', 'guest_number_children'],
    })
}


const labels = {
    owners: 'Власники',
    cancellations: "Умови скасування",
    classifications: "Класифікація",
    bookingterms: "Умови бронювання",
    icons: "Зручності",
    locations: "Місцерозташування",
    service_commons: 'Додаткові послуги',
    service_mains: "Основні послуги",
    square: 'Площа, кв.м2',
    guest_number: "Кількість гостей",
    guest_number_adults: "Кількість дорослих",
    guest_number_children: "Кількість дітей",
}

const ownerLabel = (owner) => {
    const fullName = [owner.name, owner.last_name, owner.second_name]
        .filter(value => typeof value === 'string' && value.trim())
        .join(' ');
    const label = fullName || owner.email || String(owner.id || '');
    return owner.is_active === false ? `${label} (відключений)` : label;
};

function CalendarPage() {
    const data = useLoaderData();
    const date_range = useState({
        startDate: new Date(),
        endDate: addMonths(new Date(), 3),
        key: 'selection',
    });

    return (
        <>
            <PageTitle title="Календар бронювань" />
            <React.Suspense
                fallback={<CenteredSpinner/>}
            >
                <Await
                    resolve={useMemo(() => Promise.all([data.promise]), [data])}
                    errorElement={
                        <ErrorBoundary/>
                    }>
                    {([promise]) => {
                        return (
                            <Box sx={{px: 2}}>

                                {promise && promise.data?.filters && (
                                    <FiltersComponent
                                        filters={{select: promise.data.filters, int: data.int_filters}}
                                        dateRange={date_range}
                                        currentFilters={data.currentFilter}/>

                                )}
                                {promise && promise.data?.data?.length > 0 ? (
                                    <>
                                        <CalendarTable
                                            data={promise.data.data}
                                            startDate={date_range[0].startDate}
                                            endDate={date_range[0].endDate}/>
                                        <Box sx={{mt: 2}}>
                                            <Stack sx={{
                                                '& > .MuiBox-root': {
                                                    padding: 1,
                                                    borderRadius: 2 * 8,
                                                    display: 'inline-flex',
                                                    alignItems: 'center'
                                                }
                                            }} direction='row' spacing={1}>
                                                <Box className='calendarColor1'>Модуль бронювання</Box>
                                                <Box className='calendarColor3'>Booking</Box>
                                                <Box className='calendarColor2'>Hotels24</Box>
                                                <Box className='calendarColor0'>Закрито (для бронювання)</Box>
                                                <Box className='calendarColor4'>Овербукінг</Box>
                                            </Stack>
                                        </Box>
                                    </>
                                ) : (
                                    <Box className='fullwh flex abs-center'>
                                        <Box>
                                            Не знайдено об'єктів по заданим фільтрам.
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            //{hotel_obj}
                            //<>{JSON.stringify(hotel_obj)}{fetched_table}</>//{ calendar_table }
                        )
                    }}
                </Await>
            </React.Suspense>
        </>
    );
}

export default CalendarPage;
/*
sampleData = {
    'surface':{
        'scars':{'quantity':10, color:'#ff0000'},
        'sharp edges':{'quantity':2, color:'#eee'},,
    },
    'functional':{
        'not working':{'quantity':15, color:'#ff00ff'},
        'missing assembly pieces':{'quantity':5, color:'#0000ff'},
    }
}
*/

const FiltersComponent = ({filters, dateRange, currentFilters}) => {
    const [selectionRange, setSelectionRange] = dateRange;

    const handleSelectDateRange = (ranges) => {
        setSelectionRange(ranges.selection);
    };

    const [showRangeModal, setShowRangeModal] = useState(false);
    const handleModalRangePicker = () => {
        setShowRangeModal(prev => !prev);
    }

    /*useEffect(() => {
        if (currentFilters)
            Object.entries(currentFilters).forEach(([key, value]) => {
                document.getElementById(`id-${key}-input`).value = value;
            })
    }, [])*/
    const daysShort = {
        пон: 'пн',
        вів: 'вт',
        сер: 'ср',
        чтв: 'чт',
        птн: 'пт',
        суб: 'сб',
        нед: 'нд',
    };

    const isMobile = window.innerWidth <= 768; // Определение мобильного устройства

    const customLocale = {
        ...uk,
        localize: {
            ...uk.localize,
            day: (narrowDay, {width}) => {
                const daysShort = {
                    Mon: 'пн',
                    Tue: 'вт',
                    Wed: 'ср',
                    Thu: 'чт',
                    Fri: 'пт',
                    Sat: 'сб',
                    Sun: 'нд',
                };
                return width === 'abbreviated' ? daysShort[format(addDays(new Date(), narrowDay), 'eee')] : uk.localize.day(narrowDay, {width});
            },
        },
    };

    return (
        <>
            <Form method='GET'>
                <Grid container spacing={2}>
                    {Object.entries(filters.select).map(([key, value]) => (
                        <Grid item container key={key} xs={4} md={2}>
                            <FormControl fullWidth>
                                <InputLabel id={`id-${key}-label`}>{labels[key]}</InputLabel>
                                <Select
                                    name={key}
                                    multiple
                                    labelId={`id-${key}-label`}
                                    label={labels[key]}
                                    id={`id-${key}-input`}
                                    defaultValue={currentFilters[key] ?? []}
                                    renderValue={(selected) => selected.length ? value.filter(entry => selected.includes(String(entry.id)) || selected.includes(entry.id)).map(entry => entry.city ?? entry.name).join(', ') : 'Усі міста'}
                                    sx={{overflow: 'hidden'}}
                                >
                                    {value.map((entry) => (
                                        <MenuItem key={entry.id} value={entry.id}>
                                            {key === 'owners' ? ownerLabel(entry) : (entry.city ?? entry.name ?? entry.text_address)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ))}
                    {filters.int.map((el) => (
                        <Grid item container key={el} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    name={el}
                                    label={labels[el]}
                                    type='number'
                                    id={`id-${el}-input`}
                                    defaultValue={currentFilters[el] ?? ''}
                                    inputProps={{min: 0}}
                                />
                            </FormControl>
                        </Grid>
                    ))}
                    <Grid item container>
                        <Button variant='contained' type='submit'>Застосувати фільтр</Button>
                    </Grid>
                    <Grid item container>
                        <TextField
                            label='Період'
                            value={`${daysShort[format(selectionRange.startDate, 'eee', {locale: uk})]}, ${format(selectionRange.startDate, 'dd MMM', {locale: uk})} - ${daysShort[format(selectionRange.endDate, 'eee', {locale: uk})]}, ${format(selectionRange.endDate, 'dd MMM', {locale: uk})}`}
                            onClick={handleModalRangePicker}
                        />
                        <Modal
                            open={showRangeModal}
                            className='flex abs-center'
                        >
                            <Container maxWidth='md' className='flex' sx={{height: 'fit-content'}}>
                                <Grid item container className='flex abs-center' component={Paper}
                                      sx={{p: '20px', height: '100%', overflowY: 'auto'}}>
                                    <div
                                        style={{
                                            maxWidth: '100%',
                                            overflowX: 'auto',
                                            width: isMobile ? '100%' : 'auto', // Адаптация для мобильных устройств
                                            height: isMobile ? '100%' : 'auto'
                                        }}
                                    >
                                        <DateRangePicker
                                            ranges={[selectionRange]}
                                            onChange={handleSelectDateRange}
                                            locale={customLocale}
                                            showSelectionPreview={false}
                                            months={2}
                                            direction={isMobile ? 'vertical' : 'horizontal'} // Вертикально на мобильных
                                            minDate={new Date()} // Запрещаем выбор дат до текущей
                                        />
                                    </div>
                                        <Button fullWidth
                                                variant='contained'
                                                sx={{color: 'white'}}
                                                endIcon={<DoneIcon/>}
                                                color='buttonSuccess'
                                                onClick={handleModalRangePicker}>
                                            <Typography>Готово</Typography>
                                        </Button>
                                </Grid>
                            </Container>
                        </Modal>
                    </Grid>

                </Grid>
            </Form>
            <Form method='get'>
                <Button variant='contained' type='submit'>Скинути фільтр</Button>
            </Form></>
)
} 
